import React, { useState } from 'react';
import { Field } from 'react-final-form';
import { Box, makeStyles } from '@material-ui/core';
import TextField from '@material-ui/core/TextField';
import { default as MuiAutocomplete } from '@material-ui/lab/Autocomplete';
import PropTypes from 'prop-types';

const useStyles = makeStyles(() => ({
    input: {
        '& input': {
            marginLeft: '20px',
        },
    },
}));

const AutocompleteWrapper = ({
    input: { value, onChange, multiple }, // Injected by wrapper Field from react-final-from
    meta,
    name,
    disabled,
    options,
    placeholder,
    isClearDisabled,
    helperText,
    isLoading,
    freeSolo,
    isSearchPersist,
    variant,
    searchValue,
    selectIcon,
    onBlur,
    onChange: userOnChange,
    onFocus,
    onInputChange,
    onScroll,
}) => {
    const classes = useStyles();
    const [autoCompleteValue, setAutCompleteValue] = useState('');
    const selectOptions = options || [];
    const selectedOptions = [];
    const showError = meta.error;
    const submitError = meta.submitError;

    if (Array.isArray(value)) {
        value.forEach(v => {
            const option = selectOptions.find(option => option.value === v) || { value: v, label: v };
            if (!option && freeSolo) {
                selectOptions.push(option);
            }

            selectedOptions.push(option);
        });
    } else if (value) {
        const option = selectOptions.find(option => option.value === value);
        if (!option && freeSolo) {
            const newOption = { value: value, label: value };
            selectOptions.push(newOption);
            selectedOptions.push(newOption);
        } else {
            selectedOptions.push(option);
        }
    }

    const handleOnInputChange = event => {
        const eventValue = event?.target?.value || event?.target?.textContent;
        const option = selectOptions.find(option => Number(option.value) === Number(value));
        const optionValue = isSearchPersist ? option?.label : value;

        if (!!onInputChange) {
            onInputChange(event);
        }

        if (!eventValue) {
            setAutCompleteValue(optionValue, '');
        } else {
            setAutCompleteValue(eventValue, '');
        }
    };

    return (
        <MuiAutocomplete
            clearOnBlur={!isSearchPersist}
            disableClearable={isClearDisabled}
            disabled={disabled}
            filterOptions={options => options}
            forcePopupIcon={!isSearchPersist}
            freeSolo={freeSolo}
            getOptionLabel={option => (typeof option === 'object' && option !== null ? option.label : option)}
            getOptionSelected={(option, value) => String(option.value) === String(value.value)}
            inputValue={autoCompleteValue || searchValue || ''}
            ListboxProps={{
                style: { maxHeight: '15vh' },
                onScroll: event => {
                    onScroll && onScroll(event.currentTarget);
                },
            }}
            loading={isLoading}
            multiple={multiple}
            open={isSearchPersist ? true : undefined}
            options={selectOptions}
            renderInput={params => (
                <Box style={{ display: 'flex', alignItems: 'flex-end' }}>
                    {selectIcon}
                    <TextField
                        className={classes.input}
                        error={showError || submitError}
                        helperText={meta.error || helperText}
                        name={name}
                        placeholder={placeholder}
                        variant={variant}
                        {...params}
                    />
                </Box>
            )}
            size="small"
            value={multiple ? selectedOptions : selectedOptions[0] || null}
            onBlur={onBlur}
            onChange={(_, newValue, event) => {
                // freeSolo: true, multiple: true, new tag is added, newValue is an array of strings ['A new value']
                // freeSolo: true|false, multiple: true, an existing value is selected, newValue is array of objects [{ value: 'Option1', label: 'Option1' }]
                // freeSolo: true, multiple: true, All values are removed in multiselect, newValue is empty array []
                // freeSolo: true|false, multiple: false, an existing value is selected, newValue is an object { value: 'Existing option', label: 'Existing option' }
                // freeSolo: true, multiple: false, text is deleted in input, newValue is then null
                // freeSolo: true, multiple: false, new tag is added, newValue is a string
                const value = multiple
                    ? newValue.map(option => (option.value ? option.value : option))
                    : typeof newValue === 'object' && newValue !== null
                    ? newValue.value
                    : newValue;

                onChange(value, newValue, event);
                userOnChange && userOnChange(value, newValue, event);
            }}
            onFocus={onFocus}
            onInputChange={handleOnInputChange}
        />
    );
};

AutocompleteWrapper.propTypes = {
    input: PropTypes.object,
    meta: PropTypes.object,
    name: PropTypes.string,
    options: PropTypes.array,
    required: PropTypes.bool,
    disabled: PropTypes.bool,
    multiple: PropTypes.bool,
    textFieldProps: PropTypes.object,
    placeholder: PropTypes.string,
    freeSolo: PropTypes.bool,
    isLoading: PropTypes.bool,
    isSearchPersist: PropTypes.bool,
    isClearDisabled: PropTypes.bool,
    helperText: PropTypes.string,
    variant: PropTypes.string,
    searchValue: PropTypes.string,
    selectIcon: PropTypes.node,
    onBlur: PropTypes.func,
    onChange: PropTypes.func,
    onFocus: PropTypes.func,
    onInputChange: PropTypes.func,
    onScroll: PropTypes.func,
};

AutocompleteWrapper.defaultProps = {
    input: {},
    name: '',
    meta: {},
    options: [],
    required: false,
    disabled: false,
    multiple: false,
    textFieldProps: {},
    placeholder: '',
    freeSolo: false,
    isSearchPersist: false,
    isClearDisabled: false,
    helperText: '',
    variant: 'outlined',
    searchValue: '',
    selectIcon: null,
    isLoading: false,
    onBlur: null,
    onChange: null,
    onFocus: null,
    onInputChange: () => {},
    onScroll: null,
};

const FormPersistSelectField = ({ name, ...fieldProps }) => (
    <Field
        name={name}
        render={fieldRenderProps => <AutocompleteWrapper name={name} {...fieldRenderProps} />}
        {...fieldProps}
    />
);

FormPersistSelectField.propTypes = {
    name: PropTypes.string.isRequired,
};

FormPersistSelectField.defaultProps = {
    options: [],
    multiple: false,
    size: 'small',
};

export { AutocompleteWrapper };

export default FormPersistSelectField;
